import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog, 
  DialogContent, 
  Box, 
  Typography, 
  IconButton, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  CircularProgress,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import axios from 'axios';
import PrintIcon from '@mui/icons-material/Print';
import { printCtaCte } from '../utils/printHelpers';

// Helper to format currency exactly like the screenshot: $15,250.00
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

// Retro Styled Input Component (like Access/VB forms)
const RetroField = ({ label, value, width, align = 'left', bold = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, mb: 1 }}>
    <Typography 
      variant="body2" 
      sx={{ 
        fontFamily: '"MS Sans Serif", "Tahoma", sans-serif',
        fontSize: '0.85rem',
        color: '#000000',
        mr: 1,
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </Typography>
    <Box 
      sx={{ 
        bgcolor: '#ffffff',
        border: '1px solid #7f9db9', // Classic Windows XP/7 blue-grey border
        px: 1,
        py: 0.2,
        height: '24px',
        width: width || 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        boxShadow: 'inset 1px 1px 2px rgba(0,0,0,0.1)'
      }}
    >
      <Typography 
        sx={{ 
          fontFamily: '"MS Sans Serif", "Tahoma", sans-serif',
          fontSize: '0.85rem',
          fontWeight: bold ? 'bold' : 'normal',
          color: '#000000',
          textAlign: align,
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const CtaCteDialog = ({ open, onClose, idAfiliado, authToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  const fetchCtaCte = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/afiliado/${idAfiliado}/ctacte`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (response.data.success) {
        setData(response.data);
        // Select the last row by default (like the screenshot highlights the bottom row)
        if (response.data.rows && response.data.rows.length > 0) {
          setSelectedRowIndex(response.data.rows.length - 1);
        }
      } else {
        setError(response.data.error || 'Error desconocido al cargar cuenta corriente');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Error de red o del servidor al cargar cuenta corriente');
    } finally {
      setLoading(false);
    }
  }, [idAfiliado, authToken]);

  useEffect(() => {
    if (open && idAfiliado) {
      fetchCtaCte();
    } else {
      setData(null);
      setError(null);
      setSelectedRowIndex(null);
    }
  }, [open, idAfiliado, fetchCtaCte]);

  const handleSelectRow = (index) => {
    setSelectedRowIndex(index);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          border: '3px solid #0056b3',
          borderRadius: 1,
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          bgcolor: '#e9ecef', // Vintage grey background
          p: 0,
          fontFamily: '"MS Sans Serif", "Tahoma", sans-serif'
        }
      }}
    >
      {/* Title Bar (Classic Windows style) */}
      <Box 
        sx={{ 
          background: 'linear-gradient(90deg, #0056b3 0%, #3a86c8 100%)',
          px: 1.5,
          py: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #004494'
        }}
      >
        <Typography 
          sx={{ 
            color: '#ffffff', 
            fontWeight: 'bold', 
            fontSize: '0.95rem',
            fontFamily: '"MS Sans Serif", "Tahoma", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          ▲ Consulta de Cuenta Corriente
        </Typography>
        <IconButton 
          size="small" 
          onClick={onClose} 
          sx={{ 
            color: '#ffffff', 
            bgcolor: '#d9534f', 
            borderRadius: '4px',
            width: '20px',
            height: '20px',
            p: 0,
            '&:hover': { bgcolor: '#c9302c' }
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2, overflowX: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error" gutterBottom>{error}</Typography>
            <Button variant="outlined" onClick={fetchCtaCte} sx={{ mt: 2 }}>Reintentar</Button>
          </Box>
        ) : data ? (
          <Box>
            {/* "Por Afiliado" Tab Indicator */}
            <Box sx={{ display: 'flex', mb: '-1px', zIndex: 2, position: 'relative' }}>
              <Box 
                sx={{ 
                  bgcolor: '#e9ecef', 
                  border: '1px solid #7f9db9',
                  borderBottom: 'none',
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px',
                  px: 2,
                  py: 0.5,
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  color: '#0056b3',
                  boxShadow: '0 -2px 5px rgba(0,0,0,0.05)'
                }}
              >
                Por Afiliado
              </Box>
            </Box>

            {/* Header Form Area */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                mb: 2, 
                bgcolor: '#f8f9fa', 
                border: '1px solid #7f9db9', 
                borderRadius: '0px 4px 4px 4px',
                boxShadow: '2px 2px 5px rgba(0,0,0,0.05)'
              }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={7} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  <RetroField 
                    label="Afiliado" 
                    value={data.header.id_afiliado} 
                    width="80px" 
                    bold 
                  />
                  <RetroField 
                    label="" 
                    value={data.header.nombre} 
                    width="350px" 
                    bold 
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <RetroField 
                    label="Estado" 
                    value={data.header.estado} 
                    width="40px" 
                    align="center"
                    bold
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <RetroField 
                    label="Total Afiliacion" 
                    value={formatCurrency(data.header.total_afiliacion)} 
                    width="140px" 
                    align="right"
                    bold
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Table / Grid Area */}
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{ 
                maxHeight: '380px', 
                border: '1px solid #7f9db9',
                borderRadius: 0,
                bgcolor: '#ffffff',
                boxShadow: 'inset 2px 2px 4px rgba(0,0,0,0.08)'
              }}
            >
              <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  {/* Spanned Sub-headers */}
                  <TableRow sx={{ height: 24 }}>
                    <TableCell 
                      align="center" 
                      colSpan={2}
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderRight: '1px solid #b5b5b5',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      Periodo
                    </TableCell>
                    <TableCell 
                      align="left"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderRight: '1px solid #b5b5b5',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      Compr.
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderRight: '1px solid #b5b5b5',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      Fecha
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderRight: '1px solid #b5b5b5',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      Ref.Pago
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderRight: '1px solid #b5b5b5',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      A Cobrar
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderRight: '1px solid #b5b5b5',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      Cobrado
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderRight: '1px solid #b5b5b5',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      Saldo
                    </TableCell>
                    <TableCell 
                      align="center" 
                      colSpan={2}
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        py: 0.2,
                        px: 1,
                        bgcolor: '#e0e0e0',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#000000'
                      }}
                    >
                      Detalle de Pago
                    </TableCell>
                  </TableRow>
                  {/* Secondary Header Row for Spanned Column Details */}
                  <TableRow sx={{ height: 20 }}>
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell sx={{ display: 'none' }} />
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        py: 0.1,
                        px: 1,
                        bgcolor: '#e9ecef',
                        borderRight: '1px solid #c8c8c8',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#555555'
                      }}
                    >
                      Fecha
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontFamily: '"Tahoma", sans-serif',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        py: 0.1,
                        px: 1,
                        bgcolor: '#e9ecef',
                        borderBottom: '1px solid #b5b5b5',
                        color: '#555555'
                      }}
                    >
                      Caja
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.map((row, index) => {
                    const isSelected = index === selectedRowIndex;
                    return (
                      <TableRow 
                        key={index}
                        onClick={() => handleSelectRow(index)}
                        sx={{ 
                          height: 24,
                          cursor: 'pointer',
                          bgcolor: isSelected ? '#3a86c8' : (index % 2 === 0 ? '#ffffff' : '#f9f9f9'),
                          color: isSelected ? '#ffffff' : '#000000',
                          '&:hover': {
                            bgcolor: isSelected ? '#3a86c8' : '#e2f0fb'
                          }
                        }}
                      >
                        {/* Period Month */}
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '40px'
                          }}
                        >
                          {row.mes}
                        </TableCell>
                        {/* Period Year */}
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '50px'
                          }}
                        >
                          {row.anio}
                        </TableCell>
                        {/* Compr */}
                        <TableCell 
                          align="left"
                          sx={{ 
                            fontFamily: '"MS Sans Serif", "Tahoma", sans-serif',
                            fontSize: '0.8rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '60px'
                          }}
                        >
                          {row.compr}
                        </TableCell>
                        {/* Fecha */}
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '90px'
                          }}
                        >
                          {row.fecha}
                        </TableCell>
                        {/* Ref Pago */}
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '90px'
                          }}
                        >
                          {row.ref_pago}
                        </TableCell>
                        {/* A Cobrar */}
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '100px'
                          }}
                        >
                          {formatCurrency(row.importe)}
                        </TableCell>
                        {/* Cobrado */}
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '100px'
                          }}
                        >
                          {row.imppag > 0 ? formatCurrency(row.imppag) : ''}
                        </TableCell>
                        {/* Saldo */}
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            fontWeight: row.saldo > 0 ? 'bold' : 'normal',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: row.saldo > 0 ? (isSelected ? '#ffc107' : '#d9534f') : 'inherit',
                            width: '100px'
                          }}
                        >
                          {row.saldo > 0 ? formatCurrency(row.saldo) : ''}
                        </TableCell>
                        {/* Detalle Pago Fecha */}
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '90px'
                          }}
                        >
                          {row.fecpag || '//'}
                        </TableCell>
                        {/* Detalle Pago Caja */}
                        <TableCell 
                          align="center"
                          sx={{ 
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            py: 0.2,
                            px: 1,
                            borderBottom: '1px solid #e0e0e0',
                            color: 'inherit',
                            width: '45px'
                          }}
                        >
                          {row.id_caja}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {data.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                        No se encontraron registros en la cuenta corriente.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Classic Access/VB Record Navigation Control and Totals Footer */}
            <Box 
              sx={{ 
                mt: 1.5, 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between', 
                alignItems: 'center',
                gap: 2,
                bgcolor: '#e0e0e0',
                p: 1,
                border: '1px solid #7f9db9'
              }}
            >
               {/* Access Navigation Bar & Print Button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #a0a0a0', bgcolor: '#f0f0f0', p: 0.2 }}>
                  <IconButton 
                    size="small" 
                    disabled={data.rows.length === 0 || selectedRowIndex === 0}
                    onClick={() => setSelectedRowIndex(0)}
                    sx={{ borderRadius: 0, p: 0.3 }}
                  >
                    <FirstPageIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small"
                    disabled={data.rows.length === 0 || selectedRowIndex === 0}
                    onClick={() => setSelectedRowIndex(prev => prev - 1)}
                    sx={{ borderRadius: 0, p: 0.3 }}
                  >
                    <KeyboardArrowLeftIcon fontSize="small" />
                  </IconButton>

                  {/* Text Field Indicator */}
                  <Box 
                    sx={{ 
                      bgcolor: '#ffffff', 
                      border: '1px solid #7f9db9', 
                      px: 1, 
                      mx: 0.5, 
                      height: '20px', 
                      display: 'flex', 
                      alignItems: 'center' 
                    }}
                  >
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {data.rows.length > 0 ? (selectedRowIndex + 1) : 0}
                    </Typography>
                  </Box>

                  <IconButton 
                    size="small"
                    disabled={data.rows.length === 0 || selectedRowIndex === data.rows.length - 1}
                    onClick={() => setSelectedRowIndex(prev => prev + 1)}
                    sx={{ borderRadius: 0, p: 0.3 }}
                  >
                    <KeyboardArrowRightIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small"
                    disabled={data.rows.length === 0 || selectedRowIndex === data.rows.length - 1}
                    onClick={() => setSelectedRowIndex(data.rows.length - 1)}
                    sx={{ borderRadius: 0, p: 0.3 }}
                  >
                    <LastPageIcon fontSize="small" />
                  </IconButton>

                  <Box sx={{ borderLeft: '1px solid #a0a0a0', height: '18px', mx: 0.5 }} />

                  <IconButton size="small" sx={{ borderRadius: 0, p: 0.3 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={() => printCtaCte(data.header, data.rows, data.totales)}
                  sx={{ 
                    textTransform: 'none',
                    bgcolor: 'success.main',
                    boxShadow: 1,
                    '&:hover': {
                      bgcolor: 'success.dark',
                      boxShadow: 2
                    }
                  }}
                >
                  Imprimir
                </Button>
              </Box>

              {/* Totals Section */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                <RetroField 
                  label="Totales" 
                  value={formatCurrency(data.totales.a_cobrar)} 
                  width="110px" 
                  align="right"
                  bold
                />
                <RetroField 
                  label="" 
                  value={formatCurrency(data.totales.cobrado)} 
                  width="110px" 
                  align="right"
                  bold
                />
                <RetroField 
                  label="" 
                  value={formatCurrency(data.totales.saldo)} 
                  width="110px" 
                  align="right"
                  bold
                />
              </Box>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default CtaCteDialog;
